'use client';
import { useTranslations } from 'next-intl';
import { Session } from 'next-auth';
import { Link, usePathname } from '@/src/i18n/navigation';
import { Routes } from '@/src/routes/routes';
import DynamicLink from './DynamicLink';
import { ChangeMobileLang } from '@/src/components/change-lang';
import AuthButton from './AuthButton';
import WalletComponent from './WalletComponent';
import OrderActive from './OrderComponent';
import PrimeComponent from './PrimeComponent';
import { AmplitudeEvents } from '@/src/lib/amplitude/events';
import { FC } from 'react';
import { TCountryId } from '@/src/components/sarComponent';
import { Heart } from 'lucide-react';
import { cn } from '@/src/lib/utils/command';
import { trackEvent } from '@/src/lib/amplitude';

type Props = {
  session: Session;
  walletBalance: number;
  countryId?: TCountryId;
  orderCount: number;
};

const Account: FC<Props> = ({ orderCount, session, walletBalance, countryId }) => {
  const t = useTranslations('account');
  const pathname = usePathname();
  const isActive = (path: string) => {
    return pathname === path;
  };

  return (
    <section className="mb-2 h-full w-full justify-start py-4 lg:flex lg:py-0">
      <div className="flex w-full flex-col justify-start bg-white">
        {session?.user ? (
          <>
            <div className="text-primary flex flex-row items-center space-x-3 px-3 text-base font-extrabold rtl:space-x-reverse">
              <h1>
                {t('hello')}, {session.fullName}
              </h1>

              <PrimeComponent />
            </div>

            <div className="gap-2">
              <div className="my-3 flex w-full flex-wrap items-start gap-2 lg:flex-nowrap">
                <WalletComponent walletBalance={walletBalance} countryId={countryId} />

                <div className="flex w-full items-center justify-center rounded-lg border border-solid border-[#E7E7E8] px-4 py-3">
                  {/* Order */}

                  <OrderActive orderCount={orderCount} />

                  {/* wishlist */}
                  <Link
                    href={Routes.wishlist}
                    className={cn(isActive(Routes.wishlist) ? 'opacity-100' : 'opacity-70')}
                    onClick={() => trackEvent(AmplitudeEvents.my_wishlist_clicked)}
                  >
                    <div className="flex w-28 flex-col items-center justify-center space-y-2 px-3">
                      <Heart
                        size={24}
                        absoluteStrokeWidth
                        fill={isActive(Routes.wishlist) ? '#000' : 'none'}
                      />
                      <h2 className={isActive(Routes.wishlist) ? 'font-bold' : ''}>
                        {t('wishlist')}
                      </h2>
                    </div>
                  </Link>
                </div>
              </div>
              <div className="mb-3 w-full space-y-5 rounded-lg border border-[#E7E7E8] px-3 py-4">
                <DynamicLink
                  href={Routes.profile}
                  text={t('profile')}
                  imageSrc="/assets/Person.svg"
                  imageAlt="Person"
                  amplutideEvent={AmplitudeEvents.edit_account_clicked}
                />

                <DynamicLink
                  href={Routes.prime}
                  text={t('prime')}
                  imageSrc="/assets/prime.svg"
                  imageAlt="prime"
                  amplutideEvent={AmplitudeEvents.prime_clicked}
                />

                <DynamicLink
                  href={Routes.addresses}
                  text={t('addresses')}
                  imageSrc="/assets/location.svg"
                  imageAlt="location"
                  amplutideEvent={AmplitudeEvents.my_addresses_clicked}
                />

                <DynamicLink
                  href={Routes.reportedIssues}
                  text={t('ReportedIssues')}
                  imageSrc="/assets/headpho.svg"
                  imageAlt="Reported Issues"
                  amplutideEvent={AmplitudeEvents.reported_issues_clicked}
                />

                <DynamicLink
                  href={Routes.payment}
                  text={t('payment')}
                  imageSrc="/assets/Wallet.svg"
                  imageAlt="Wallet"
                  amplutideEvent={AmplitudeEvents.my_saved_cards_clicked}
                />
              </div>
            </div>
          </>
        ) : null}

        <div className="mt-6 w-full space-y-5 rounded-lg border border-[#E7E7E8] px-3 py-4">
          <DynamicLink
            href={Routes.contactUs}
            text={t('contact-us')}
            imageSrc="/assets/phone.svg"
            imageAlt="phone"
            amplutideEvent={AmplitudeEvents.contact_us_clicked}
          />

          <DynamicLink
            href={Routes.aboutUs}
            text={t('about-pik')}
            imageSrc="/assets/about.svg"
            imageAlt="about"
            amplutideEvent={AmplitudeEvents.about_pik_clicked}
          />
          <DynamicLink
            href={Routes.joinUs}
            text={t('join-us')}
            imageSrc="/assets/handshake-deal-square.svg"
            imageAlt="join us"
            amplutideEvent={AmplitudeEvents.join_us}
          />
        </div>

        <div className="mt-2">
          <ChangeMobileLang />
        </div>

        <div className="my-2">
          <AuthButton login={t('login')} signUp={t('logout')} session={session} />
        </div>
      </div>
    </section>
  );
};

export default Account;
